import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/*
 * ⚠️ NUVIA — ESTE ARCHIVO ES DE HORIZON Y ESTA MODIFICADO. Un `shopify theme pull` lo pisa.
 * Si vuelve a aparecer sin estas marcas, el carrito volvio a perder unidades.
 * Lo que se le cambio (27-ago, pendiente -2 de docs/ESTADO-Y-PENDIENTES.md):
 *
 *   1. UN TEMPORIZADOR POR FILA, no uno para todo el carrito. Horizon traia un solo
 *      debounce de 300 ms compartido: cambiar dos filas dentro de esa ventana descartaba
 *      la primera — la pantalla mostraba el numero nuevo y la peticion no salia NUNCA.
 *      Reproducido en tests/test-carrito-filas.mjs (caso 1).
 *
 *   2. LA FILA VIAJA POR SU VARIANTE (`id: <variant_id>`), NO POR SU POSICION (`line: n`).
 *      Horizon mandaba la posicion y sacaba la fila del DOM antes de que contestara el
 *      servidor, sin renumerar las que quedaban. Repro medido (caso 3): con el "+" de
 *      Blue Stripes pendiente y el tacho de Azure Dream, el "+" terminaba subiendole la
 *      unidad a COZY SILK. Es el mecanismo clasico de "se borro la que no era".
 *
 *      ⛔ NO usar `item.key` para esto, aunque parezca lo natural: MEDIDO contra la tienda
 *      el 27-ago, Shopify le REGENERA la clave a TODAS las lineas en cada escritura del
 *      carrito. Un `id` con la clave leida del DOM vuelve 400 "Identificación o parámetro
 *      lineal no válidos" apenas otra fila se movio antes. El id de variante, en cambio,
 *      no cambia nunca y sobrevive al renumerado (probado con borrado de por medio).
 *      La variante solo se usa cuando es UNICA en el carrito; si dos renglones la
 *      comparten (mismo producto con propiedades distintas, un combo) Shopify le pegaria
 *      al primero, asi que ahi se vuelve a la posicion.
 *
 *   3. LAS PETICIONES SALEN DE A UNA (cola). Con el arreglo 1 pueden quedar dos en vuelo,
 *      y la respuesta mas lenta repintaba el carrito con un estado viejo: la clienta
 *      volvia a ver desaparecer su unidad, ahora solo en pantalla.
 *
 * Todo lo demas quedo tal cual vino de Horizon.
 */

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  /**
   * NUVIA (1): un temporizador POR FILA. Horizon tenia uno solo para todo el carrito.
   * El cajon se rotula con el id de VARIANTE, que sobrevive a los repintados; con la clave
   * del renglon no serviria, porque Shopify se la cambia en cada escritura y la misma fila
   * caeria en un cajon nuevo cada vez.
   * @type {Map<string, ReturnType<typeof debounce>>}
   */
  #porFila = new Map();

  /**
   * NUVIA (3): las peticiones al carrito salen de a una.
   * @type {Promise<void>}
   */
  #cola = Promise.resolve();
  #enVuelo = 0;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);

    for (const pendiente of this.#porFila.values()) pendiente.cancel();
    this.#porFila.clear();
  }

  /**
   * NUVIA — La clave del renglon del carrito al que pertenece un elemento.
   * Se resuelve SUBIENDO desde el elemento que disparo el evento, nunca por indice: el
   * indice es justo lo que queda desactualizado cuando otra fila se borra del DOM.
   * @param {EventTarget | null} node
   * @returns {string | null}
   */
  #claveDeFila(node) {
    const fila = node instanceof Element ? node.closest('[data-key]') : null;
    const clave = fila instanceof HTMLElement ? fila.dataset.key : '';
    return clave ? clave : null;
  }

  /**
   * NUVIA — El identificador que viaja al servidor: el id de VARIANTE del renglon.
   * Se devuelve solo si esa variante aparece en UNA sola fila; si esta repetida, Shopify
   * le pegaria a la primera y ahi la posicion es mas fiel. Ver el encabezado del archivo:
   * la clave del renglon NO sirve, Shopify la regenera en cada escritura.
   * @param {EventTarget | null} node
   * @returns {string | null}
   */
  #idDeFila(node) {
    const fila = node instanceof Element ? node.closest('[data-key]') : null;
    const conVariante = fila?.querySelector('[data-variant-id]');
    const variante = conVariante instanceof HTMLElement ? conVariante.dataset.variantId : '';
    if (!variante) return null;

    const filas = this.refs.cartItemRows ?? [];
    const repetida = filas.filter((row) => row.querySelector(`[data-variant-id="${variante}"]`)).length > 1;
    return repetida ? null : variante;
  }

  /**
   * NUVIA — El renglon del carrito: por clave, si no por variante, y recien al final por
   * posicion. La clave es exacta pero se pudre apenas hay una escritura de por medio
   * (Shopify la regenera); la variante aguanta el repintado. La posicion queda de ultima
   * porque es justo la que se desalinea cuando otra fila se fue.
   * @param {number} line
   * @param {string | null} clave
   * @param {string | null} [idServidor]
   * @returns {HTMLTableRowElement | undefined}
   */
  #filaDe(line, clave, idServidor) {
    const filas = this.refs.cartItemRows ?? [];
    if (clave) {
      const porClave = filas.find((row) => row.dataset.key === clave);
      if (porClave) return porClave;
    }
    if (idServidor) {
      const porVariante = filas.filter((row) => row.querySelector(`[data-variant-id="${idServidor}"]`));
      if (porVariante.length === 1) return porVariante[0];
    }
    return filas[line - 1];
  }

  /**
   * NUVIA — Recibe el evento del selector de cantidad y lo encola en el temporizador de SU
   * fila. El filtro de pertenencia va aca, antes del temporizador: con uno compartido, un
   * evento del drawer podia hacer que la pagina del carrito descartara el suyo.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantitySelectorUpdate = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    const clave = this.#claveDeFila(event.target);
    const idServidor = this.#idDeFila(event.target);
    const cajon = idServidor ?? `pos:${line}`;

    let pendiente = this.#porFila.get(cajon);
    if (!pendiente) {
      pendiente = debounce(
        (/** @type {{line: number, quantity: number, clave: string | null, idServidor: string | null}} */ dato) => {
          this.#onQuantityChange(dato);
        },
        300
      );
      this.#porFila.set(cajon, pendiente);
    }

    pendiente({ line, quantity, clave, idServidor });
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {{line: number, quantity: number, clave: string | null, idServidor: string | null}} dato
   */
  #onQuantityChange({ line, quantity, clave, idServidor }) {
    if (quantity === 0) {
      return this.#quitarLinea(line, clave, idServidor);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
      clave,
      idServidor,
    });
    const lineItemRow = this.#filaDe(line, clave, idServidor);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * Lo llama el tacho por `on:click="/onLineItemRemove/{{ line }}"`, con el evento de
   * segundo argumento: de ese elemento sale el renglon, y del renglon el id de variante
   * que de verdad viaja al servidor. La posicion del atributo queda solo de reserva.
   * @param {number} line - The line item index.
   * @param {Event} [event] - The click event, when it comes from the remove button.
   */
  onLineItemRemove(line, event) {
    const desde = event?.target ?? null;
    this.#quitarLinea(line, this.#claveDeFila(desde), this.#idDeFila(desde));
  }

  /**
   * @param {number} line - The line item index.
   * @param {string | null} clave - The line item key (solo para encontrar el <tr>).
   * @param {string | null} idServidor - El id de variante que viaja al servidor.
   */
  #quitarLinea(line, clave, idServidor) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      clave,
      idServidor,
    });

    const cartItemRowToRemove = this.#filaDe(line, clave, idServidor);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string | null} [config.clave] - NUVIA: la clave del renglon, solo para el DOM.
   * @param {string | null} [config.idServidor] - NUVIA: el id de variante que viaja al servidor.
   */
  updateQuantity(config) {
    this.#disableCartItems();
    this.#enVuelo++;

    // NUVIA (3): de a una. Con dos peticiones en vuelo, la respuesta mas lenta repinta el
    // carrito con un estado viejo. El `catch` es obligatorio: sin el, una peticion que
    // falla deja la cola rechazada y las siguientes no salen nunca.
    this.#cola = this.#cola
      .then(() => this.#enviarCambio(config))
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enVuelo = Math.max(0, this.#enVuelo - 1);
        if (this.#enVuelo === 0) this.#enableCartItems();
      });
  }

  /**
   * NUVIA — El envio propiamente dicho. Es el cuerpo que Horizon tenia dentro de
   * `updateQuantity`; lo unico que cambio adentro es que la fila viaja por clave.
   * @param {{line: number, quantity: number, action: string, clave?: string | null, idServidor?: string | null}} config
   * @returns {Promise<void>}
   */
  #enviarCambio(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    const { line, quantity, clave, idServidor } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // NUVIA (2): `id` = el id de VARIANTE, que Shopify resuelve sin mirar el orden y que
    // no se regenera nunca. `line` queda de reserva para el renglon sin variante legible o
    // con la variante repetida: ahi quedamos como estabamos, nunca peor.
    const body = JSON.stringify({
      ...(idServidor ? { id: idServidor } : { line: line }),
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, clave, idServidor, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full', { injectStylesheet: true });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // NUVIA (3): desbloquear aca destrabaria el carrito con otra peticion todavia en
        // la cola. Lo hace `updateQuantity`, que es el unico que sabe cuantas quedan.
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   *
   * NUVIA — El error se pinta en la fila que FALLO, no en la que quedo en esa posicion.
   * Horizon lo direccionaba por posicion (`quantitySelectors[line - 1]` y los ref numerados),
   * y desde que la peticion viaja por variante las dos identidades pueden no coincidir: con
   * una fila borrada de por medio y el carrito ya repintado, el aviso "ya esta agotado" y el
   * reseteo de la cajita caian en la prenda vecina. La clienta se quedaba mirando una
   * cantidad que el carrito no tiene, y su proximo "+" mandaba esa cantidad absoluta.
   * Pasa de verdad en las 4 Ofertas de despedida, que son las unicas con stock real.
   * Reproducido en tests/test-carrito-filas.mjs (caso 12).
   *
   * @param {number} line - The line.
   * @param {string | null} clave - NUVIA: la clave del renglon.
   * @param {string | null} idServidor - NUVIA: el id de variante del renglon.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, clave, idServidor, parsedResponseText) => {
    const fila = this.#filaDe(line, clave, idServidor);

    const quantityInput =
      fila?.querySelector('input[ref="quantityInput"]') ??
      this.refs.quantitySelectors?.[line - 1]?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = fila?.querySelector('[ref^="cartItemError-"]') ?? this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer =
      fila?.querySelector('[ref^="cartItemErrorContainer-"]') ?? this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    // NUVIA 24-oct Q9: el 422 de stock se dice corto y se va solo a los 4 s.
    const msg = String(parsedResponseText.errors || '');
    cartItemError.textContent = /disponibilidad|agotad|solo se/i.test(msg) ? 'No hay más en esa talla' : msg;
    cartItemErrorContainer.classList.remove('hidden');
    clearTimeout(cartItemErrorContainer._nvT);
    cartItemErrorContainer._nvT = setTimeout(() => cartItemErrorContainer.classList.add('hidden'), 4000);
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml, 'full', { injectStylesheet: true });

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
